import { useDeferredValue, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'

import MemberDetailActions from './MemberDetailActions'
import MemberDetailHeader from './MemberDetailHeader'
import NotetakerErasureStatus from './NotetakerErasureStatus'

import { isApiError } from '@/api'
import { getDesktopApps, getWordsDictated } from '@/api/teamInsights'
import { getMemberDetail } from '@/api/teamMembersTable'
import { useAdminNotetakerErasureEnabled } from '@/hooks/useAdminNotetakerErasureEnabled'
import { useAnalytics } from '@/hooks/useAnalytics'
import DesktopAppsChart from '@/pages/app/UsageV2/DesktopAppsChart'
import ErrorCard from '@/pages/app/UsageV2/ErrorCard'
import AppFilter from '@/pages/app/UsageV2/filters/AppFilter'
import { resolveDateFilter } from '@/pages/app/UsageV2/filters/dateRange'
import DateRangeFilter from '@/pages/app/UsageV2/filters/DateRangeFilter'
import { filtersToParams } from '@/pages/app/UsageV2/filters/params'
import { DEFAULT_FILTERS, type UsageFiltersState } from '@/pages/app/UsageV2/filters/types'
import { SkeletonCard } from '@/pages/app/UsageV2/SkeletonCard'
import { useUtcDayIndex } from '@/pages/app/UsageV2/useUtcDayClock'
import WordsDictatedChart from '@/pages/app/UsageV2/WordsDictatedChart'
import { selectCurrentEnterprise } from '@/store/features/user/userSlice'
import { EventName } from '@/utils/eventDefinitions'

// Pick the right card state for a query: error → loading → success.
function QueryCard<T>({
  query,
  children,
}: {
  query: UseQueryResult<T>
  children: (data: T) => React.ReactNode
}): React.ReactNode {
  if (query.isError) return <ErrorCard onRetry={() => void query.refetch()} />
  if (query.isLoading) return <SkeletonCard />
  if (query.data === undefined) return null
  return children(query.data)
}

const UsageV2MemberDetailPage = () => {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const enterprise = useSelector(selectCurrentEnterprise)
  const enterpriseId = enterprise?.id ?? null
  const [filters, setFilters] = useState<UsageFiltersState>(DEFAULT_FILTERS)
  const { trackEvent } = useAnalytics()
  const hasTrackedView = useRef(false)
  const viewedFrom: 'table_row_click' | 'deep_link' =
    (location.state as { from?: unknown } | null)?.from === 'table_row_click' ? 'table_row_click' : 'deep_link'

  // All time resolves to no date bounds, so only this day key moves the charts at midnight.
  const utcDay = useUtcDayIndex()
  const params = filtersToParams(filters)
  const resolvedRange = resolveDateFilter(filters.date)
  const deferredParams = useDeferredValue(params)

  // Charts use the full filter shape (start/end/apps) but pin user_ids to the
  // page's userId; team_id is forced null so the chart isn't double-filtered.
  const chartFilters = {
    ...deferredParams,
    user_ids: userId ? [userId] : undefined,
    team_id: undefined,
  }

  const notetakerErasureEnabled = useAdminNotetakerErasureEnabled()

  // Detail call has no query params — header fields are unaffected by filters.
  const memberDetail = useQuery({
    queryKey: ['team-members', 'detail', enterpriseId, userId],
    queryFn: () => getMemberDetail({ userId: userId! }).then((r) => r.data),
    enabled: !!enterpriseId && !!userId,
  })

  const wordsDictated = useQuery({
    queryKey: ['team-insights', 'words-dictated', enterpriseId, userId, deferredParams, utcDay],
    queryFn: () => getWordsDictated(enterpriseId!, chartFilters).then((r) => r.data),
    enabled: !!enterpriseId && !!userId && memberDetail.isSuccess,
  })

  const desktopApps = useQuery({
    queryKey: ['team-insights', 'desktop-apps', enterpriseId, userId, deferredParams, utcDay],
    queryFn: () => getDesktopApps(enterpriseId!, chartFilters).then((r) => r.data),
    enabled: !!enterpriseId && !!userId && memberDetail.isSuccess,
  })

  const memberNotFound = memberDetail.isError && isApiError(memberDetail.error) && memberDetail.error.status === 404

  // Fire MemberDetailViewed once per mount after the detail call resolves —
  // post-await per the project's analytics rule. Strict Mode double-mount is
  // tolerated; the ref guards against duplicate fires.
  useEffect(() => {
    if (!userId || !memberDetail.data || hasTrackedView.current) return
    hasTrackedView.current = true
    trackEvent?.(EventName.MemberDetailViewed, {
      user_id: userId,
      viewed_from: viewedFrom,
    })
  }, [userId, memberDetail.data, trackEvent, viewedFrom])

  if (memberNotFound) {
    return (
      <div className="relative flex h-full w-full flex-col gap-8 overflow-y-auto rounded-xl bg-card px-20 py-12 font-[Figtree]">
        <Link
          to="/app/usage?tab=team-members"
          className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm leading-5"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Back to Team members
        </Link>
        <ErrorCard
          title="Member not found"
          description="This member doesn't exist in your enterprise. They may have been removed."
        />
      </div>
    )
  }

  return (
    <div className="relative flex h-full w-full flex-col gap-8 overflow-y-auto rounded-xl bg-card px-20 py-12 font-[Figtree]">
      <Link
        to="/app/usage?tab=team-members"
        className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm leading-5"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        Back to Team members
      </Link>

      {memberDetail.isError ? (
        <ErrorCard onRetry={() => void memberDetail.refetch()} />
      ) : (
        <MemberDetailHeader
          member={memberDetail.data}
          actionsSlot={
            memberDetail.data ? (
              <MemberDetailActions
                member={memberDetail.data}
                onRemoved={() => navigate('/app/usage?tab=team-members')}
              />
            ) : null
          }
        />
      )}

      {notetakerErasureEnabled && memberDetail.data && <NotetakerErasureStatus userId={memberDetail.data.userId} />}

      <div className="flex items-center gap-4">
        <DateRangeFilter value={filters.date} onChange={(date) => setFilters({ ...filters, date })} />
        <AppFilter
          enterpriseId={enterpriseId}
          appKeys={filters.appKeys}
          appCategories={filters.appCategories}
          onChange={({ appKeys, appCategories }) => setFilters({ ...filters, appKeys, appCategories })}
        />
      </div>

      <QueryCard query={wordsDictated}>
        {(data) => (
          <WordsDictatedChart
            response={data}
            dataUpdatedAt={wordsDictated.dataUpdatedAt}
            filterStart={resolvedRange.start}
            filterEnd={resolvedRange.end}
            dateFilter={filters.date}
            singleMember
          />
        )}
      </QueryCard>

      <QueryCard query={desktopApps}>
        {(data) => <DesktopAppsChart response={data} dateFilter={filters.date} singleMember />}
      </QueryCard>
    </div>
  )
}

export default UsageV2MemberDetailPage
